import Icon from "@components/ui/icon";
import {
  MediaLibPresetEnum,
  MediaPanelTabEnum,
} from "@editor/modules/media-lib/types";
import { cn } from "@lib/cn";
import { useEffect, useMemo, useRef, useState } from "react";

interface IProps {
  selectedTab: MediaPanelTabEnum;
  setSelectedTab: (value: MediaPanelTabEnum) => void;
  preset: MediaLibPresetEnum;
}

const TABS = [
  {
    label: "Stock images",
    shortLabel: "Stock Images",
    key: MediaPanelTabEnum.StockImages,
    icon: "ri-image-line",
  },
  {
    label: "Stock videos",
    shortLabel: "Videos",
    key: MediaPanelTabEnum.StockVideos,
    icon: "ri-video-line",
  },
  {
    label: "AI Generate",
    shortLabel: "AI Generate",
    key: MediaPanelTabEnum.AiGenerate,
    icon: "ri-image-ai-line",
  },
  {
    label: "My Files",
    shortLabel: "My Files",
    key: MediaPanelTabEnum.MediaLib,
    icon: "ri-folders-line",
  },
  {
    label: "Logo Finder",
    shortLabel: "Logos",
    key: MediaPanelTabEnum.LogoFinder,
    icon: "sd-logo-finder",
  },
  {
    label: "GIFs",
    shortLabel: "GIFs",
    key: MediaPanelTabEnum.Gifs,
    icon: "ri-film-line",
  },
];

export const MediaDialogTabs = ({
  selectedTab,
  setSelectedTab,
  preset,
}: IProps) => {
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 112,
  });
  const tabRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (selectedTab && tabRefs.current[selectedTab]) {
      const activeTabElement = tabRefs.current[selectedTab];
      const { offsetLeft, offsetWidth } = activeTabElement as HTMLDivElement;
      setIndicatorStyle({
        left: offsetLeft,
        width: offsetWidth,
      });
    }
  }, [selectedTab]);

  const tabs = useMemo(() => {
    return TABS.filter((item) => {
      if (preset === MediaLibPresetEnum.OnlyImages) {
        return item.key !== MediaPanelTabEnum.StockVideos;
      }
      if (preset === MediaLibPresetEnum.OnlyVideos) {
        return item.key !== MediaPanelTabEnum.StockImages;
      }
      return true;
    });
  }, [preset]);

  return (
    <div className="tw-border-b-solid tw-mb-6 tw-border-b tw-border-b-blue-100 tw-px-6 -sm:tw-mb-3 -sm:tw-px-2">
      <div className="tw-relative tw-flex tw-items-center tw-justify-between -sm:tw-justify-start -sm:tw-overflow-x-auto -sm:tw-no-scrollbar -sm:tw-gap-1">
        {tabs.map((item) => {
          const isDisabled =
            preset === MediaLibPresetEnum.OnlyVideos &&
            [
              MediaPanelTabEnum.AiGenerate,
              MediaPanelTabEnum.LogoFinder,
              MediaPanelTabEnum.Gifs,
            ].includes(item.key);
          return (
            <div
              ref={(el) => (tabRefs.current[item.key] = el)}
              onClick={() =>
                !isDisabled && setSelectedTab(item.key as MediaPanelTabEnum)
              }
              className={cn(
                "tw-group tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-px-[9px] tw-font-sans tw-text-[11px]/normal tw-font-semibold tw-text-blue-500 tw-transition-colors hover:tw-text-primary",
                "-sm:tw-shrink-0 -sm:tw-whitespace-nowrap",
                selectedTab === item.key && "tw-text-primary",
                isDisabled &&
                  "tw-cursor-not-allowed tw-opacity-50 hover:tw-text-blue-500",
              )}
              key={item.key}
            >
              <Icon
                icon={item.icon}
                className={cn(
                  "tw-mr-1.5 tw-fill-blue-500 tw-text-blue-500 tw-transition-colors group-hover:tw-fill-primary group-hover:tw-text-primary -sm:tw-hidden",
                  selectedTab === item.key && "tw-fill-primary tw-text-primary",
                  isDisabled &&
                    "group-hover:tw-fill-blue-500 group-hover:tw-text-blue-500",
                )}
              />
              <span className="-sm:tw-hidden">{item.label}</span>
              <span className="tw-hidden -sm:tw-inline">
                {item.shortLabel}
              </span>
            </div>
          );
        })}
        <span
          className="tw-absolute -tw-bottom-px tw-h-0.5 tw-bg-primary tw-transition-all tw-duration-300"
          style={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
          }}
        ></span>
      </div>
    </div>
  );
};
